import { onMounted } from 'vue'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'

export interface PerformanceMetrics {
  CLS?: number // Cumulative Layout Shift
  FCP?: number // First Contentful Paint
  INP?: number // Interaction to Next Paint (replaces FID)
  LCP?: number // Largest Contentful Paint
  TTFB?: number // Time to First Byte
}

/**
 * Composable for monitoring Web Vitals performance metrics
 *
 * @param options - Configuration options
 * @param options.enableLogging - Whether to log metrics to console (default: false in production)
 * @param options.onMetric - Callback function to handle metrics (e.g., send to analytics)
 *
 * @example
 * ```ts
 * // In App.vue or main layout component
 * import { usePerformanceMonitoring } from '@/composables/usePerformanceMonitoring'
 *
 * usePerformanceMonitoring({
 *   enableLogging: true,
 *   onMetric: (metric) => {
 *     // Send to analytics service
 *     analytics.track('web-vital', metric)
 *   }
 * })
 * ```
 */
export function usePerformanceMonitoring(options?: {
  enableLogging?: boolean
  onMetric?: (metric: Metric) => void
}) {
  const isDev = import.meta.env.DEV
  const enableLogging = options?.enableLogging ?? isDev

  const handleMetric = (metric: Metric) => {
    if (enableLogging) {
      // eslint-disable-next-line no-console
      console.log(`[Web Vitals] ${metric.name}:`, {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta
      })
    }

    // Call custom callback if provided
    if (options?.onMetric) {
      options.onMetric(metric)
    }
  }

  onMounted(() => {
    // Monitor Core Web Vitals
    onCLS(handleMetric)
    onFCP(handleMetric)
    onINP(handleMetric)
    onLCP(handleMetric)
    onTTFB(handleMetric)
  })

  return {
    // Could return methods to manually get metrics if needed
  }
}

/**
 * Get performance thresholds for Core Web Vitals
 * Based on Google's recommendations
 */
export const performanceThresholds = {
  CLS: {
    good: 0.1,
    needsImprovement: 0.25
  },
  FCP: {
    good: 1800,
    needsImprovement: 3000
  },
  INP: {
    good: 200,
    needsImprovement: 500
  },
  LCP: {
    good: 2500,
    needsImprovement: 4000
  },
  TTFB: {
    good: 800,
    needsImprovement: 1800
  }
} as const

/**
 * Evaluate metric rating based on thresholds
 */
export function evaluateMetric(name: keyof typeof performanceThresholds, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = performanceThresholds[name]
  if (value <= threshold.good) return 'good'
  if (value <= threshold.needsImprovement) return 'needs-improvement'
  return 'poor'
}
